/**
 * Mirrors Stripe's subscription status verbatim so reconciliation is a straight copy.
 * Stored as a text column - never a PG enum, which cannot be altered safely under `synchronize`.
 */
export enum SubscriptionStatus {
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  Trialing = 'trialing',
  Active = 'active',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Unpaid = 'unpaid',
  Paused = 'paused',
}

/**
 * Statuses that still grant entitlements. `past_due` is deliberately included: Stripe keeps
 * retrying the invoice, and cutting access off on the first failed charge is hostile.
 */
export const ENTITLING_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.Active,
  SubscriptionStatus.Trialing,
  SubscriptionStatus.PastDue,
]

export const isEntitlingStatus = (status: SubscriptionStatus): boolean =>
  ENTITLING_SUBSCRIPTION_STATUSES.includes(status)

/**
 * Statuses where a real subscription still exists in Stripe and must keep being managed -
 * updated, cancelled - rather than replaced with a new one.
 *
 * Wider than the entitling set on purpose: `unpaid` and `paused` grant nothing, but the
 * subscription object is alive and (for `unpaid`) still raising invoices. Treating those as
 * "no subscription" would open a second one alongside it and bill the customer twice.
 *
 * `incomplete` is deliberately excluded: that first payment never cleared, Stripe expires the
 * subscription about 23 hours later, and the right answer to a retry is a fresh Checkout.
 */
export const LIVE_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  ...ENTITLING_SUBSCRIPTION_STATUSES,
  SubscriptionStatus.Unpaid,
  SubscriptionStatus.Paused,
]

export const isLiveStatus = (status: SubscriptionStatus): boolean => LIVE_SUBSCRIPTION_STATUSES.includes(status)
